import React, { useEffect, useMemo, useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import { GenericButton } from "@ui-components/Button/GenericButton";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { DateTimeField, FIELD_CLASS, FormErrors, RequestError } from "./MaintenanceFormFields";
import {
	buildUpdatePayload,
	editFormFromRecord,
	replaceWindowLine,
	validateEditForm,
	type MaintenanceRecordEditForm,
} from "./maintenanceWindowsModel";
import { describeRequestError, updateMaintenanceRecord } from "./requests/maintenanceWindowsApi";
import type { MaintenanceRecordRow } from "./types";

const EMPTY_EDIT_FORM: MaintenanceRecordEditForm = {
	startsAtLocal: "",
	endsAtLocal: "",
	description: "",
};

/**
 * Edit ONE customer's copy of a window (CTX-1294). The other customers of the
 * same window keep theirs: rows are not linked, so a change here is a change
 * for this customer only, and the modal says so.
 *
 * The description is saved verbatim. Moving the hours rewrites its "Window:"
 * line to match, so the text cannot keep announcing the old ones.
 */
const EditMaintenanceRecordModal: React.FC<{
	record: MaintenanceRecordRow | null;
	onClose: () => void;
	onSaved: (saved: MaintenanceRecordRow) => void;
}> = ({ record, onClose, onSaved }) => {
	const api = useAxiosAuth0();
	const [form, setForm] = useState<MaintenanceRecordEditForm>(EMPTY_EDIT_FORM);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [attempted, setAttempted] = useState(false);

	useEffect(() => {
		if (!record) return;
		setForm(editFormFromRecord(record));
		setError(null);
		setAttempted(false);
	}, [record]);

	const errors = useMemo(() => validateEditForm(form), [form]);

	const updateTime = (field: "startsAtLocal" | "endsAtLocal", value: string) =>
		setForm((prev) => {
			const next = { ...prev, [field]: value };
			return {
				...next,
				description: replaceWindowLine(next.description, next.startsAtLocal, next.endsAtLocal),
			};
		});

	const handleSave = async () => {
		setAttempted(true);
		if (!record || errors.length > 0) return;
		setSubmitting(true);
		setError(null);
		try {
			onSaved(await updateMaintenanceRecord(api, record.id, buildUpdatePayload(form)));
			onClose();
		} catch (err) {
			setError(describeRequestError(err));
		} finally {
			setSubmitting(false);
		}
	};

	if (!record) return null;

	return (
		<GenericModal
			isOpen
			onClose={onClose}
			size="lg"
			title={
				<div className="flex flex-col min-w-0">
					<span className="text-h5 font-bold">Edit maintenance</span>
					<span className="text-sm font-normal text-semantic-text-secondary mt-0.5">
						For {record.customer?.name ?? `tenant #${record.tenantId}`} only — other
						customers of this window keep their copy
					</span>
				</div>
			}
			footer={
				<div className="flex items-center gap-2 w-full">
					<div className="flex-1" />
					<GenericButton
						label="Cancel"
						variant="secondary"
						size="md"
						onClick={onClose}
						disabled={submitting}
					/>
					<GenericButton
						label="Save changes"
						variant="brand"
						size="md"
						loading={submitting}
						disabled={submitting || (attempted && errors.length > 0)}
						onClick={() => {
							void handleSave();
						}}
					/>
				</div>
			}
		>
			<div className="flex flex-col gap-5">
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
					<DateTimeField
						id="maintenance-edit-starts-at"
						label="Starts"
						value={form.startsAtLocal}
						onChange={(value) => updateTime("startsAtLocal", value)}
					/>
					<DateTimeField
						id="maintenance-edit-ends-at"
						label="Ends"
						value={form.endsAtLocal}
						onChange={(value) => updateTime("endsAtLocal", value)}
					/>
				</div>
				<div className="flex flex-col space-y-2">
					<label
						htmlFor="maintenance-edit-description"
						className="text-sm font-medium text-semantic-text-primary"
					>
						Description
						<span className="ml-2 font-normal text-semantic-text-secondary">
							saved exactly as written
						</span>
					</label>
					<textarea
						id="maintenance-edit-description"
						rows={8}
						value={form.description}
						onChange={(event) =>
							setForm((prev) => ({ ...prev, description: event.target.value }))
						}
						className={FIELD_CLASS}
					/>
				</div>
				{attempted && <FormErrors errors={errors} />}
				<RequestError message={error} />
			</div>
		</GenericModal>
	);
};

export default EditMaintenanceRecordModal;
